/** The player's towns, under the hero strip.
 *
 * A town row answers three questions at a glance: which faction, whether it has
 * built today, and whether a hero is standing in it. Everything else is a click
 * away in the town screen, which another agent owns.
 */

import { useCallback, useMemo } from "react";
import type { ContentRegistry, FactionId, ObservedTown, TownId } from "@game/core";
import { BitmapText, romanFaceForStep, useBitmapScale } from "../theme/bitmapText.js";
import { Frame, Icon, Panel, ScrollArea, cx } from "../theme/components.js";
import { METRICS, playerColour } from "../theme/tokens.js";
import { useContent, useObservation } from "../hooks/useGameState.js";
import { ownTowns } from "./select.js";
import type { HoverTarget } from "./types.js";

export interface TownListProps {
  readonly selectedTownId: TownId | null;
  readonly onSelect: (id: TownId) => void;
  /** Double-click, or the "enter town" button, opens the town screen. */
  readonly onOpen?: ((id: TownId) => void) | undefined;
  readonly onHover?: ((target: HoverTarget) => void) | undefined;
  readonly className?: string | undefined;
}

export function TownList(props: TownListProps): JSX.Element {
  const obs = useObservation();
  const content = useContent();
  const towns = useMemo(() => ownTowns(obs), [obs]);
  // Same media step as the hero roster above it; two racks one under the other
  // at two magnifications would be worse than either.
  const nameScale = useBitmapScale(2);
  const clearHover = useCallback(() => props.onHover?.({ kind: "NONE" }), [props]);

  return (
    <Panel className={cx("hm-col", "hm-adv-band", props.className)} style={{ minHeight: 0 }}>
      {/* ~45 device pixels of heading for one entry was the whole TOWNS band's
          problem. Same caption rule as the hero band. */}
      <div className="hm-adv-band__title">
        <BitmapText face="roman" scale={1}>Towns</BitmapText>
        <span className="hm-adv-band__count">
          <BitmapText face="roman" scale={1}>{towns.length}</BitmapText>
        </span>
      </div>
      <ScrollArea className="hm-grow" onPointerLeave={clearHover}>
        <div className="hm-col" style={{ gap: "calc(var(--hm-u) * 2)" }}>
          {towns.length === 0 && (
            <span className="hm-label" style={{ padding: "var(--hm-pad)" }}>
              No towns. Capture one to keep playing.
            </span>
          )}
          {towns.map((town) => (
            <TownRow
              key={town.id}
              town={town}
              selected={town.id === props.selectedTownId}
              factionName={factionName(content, town.faction)}
              colour={playerColour(obs.self?.colour ?? null)}
              nameScale={nameScale}
              onSelect={props.onSelect}
              onOpen={props.onOpen}
              onHover={props.onHover}
            />
          ))}
        </div>
      </ScrollArea>
    </Panel>
  );
}

function factionName(content: ContentRegistry | null, faction: string): string {
  const def = content?.factions.get(faction as FactionId);
  return def?.name ?? faction.replace(/\b[a-z]/g, (c) => c.toUpperCase());
}

function TownRow(props: {
  readonly town: ObservedTown;
  readonly selected: boolean;
  readonly factionName: string;
  readonly colour: string;
  /** Magnification for the stamped name. */
  readonly nameScale: number;
  readonly onSelect: (id: TownId) => void;
  readonly onOpen?: ((id: TownId) => void) | undefined;
  readonly onHover?: ((t: HoverTarget) => void) | undefined;
}): JSX.Element {
  const { town } = props;
  const open = useCallback(() => props.onOpen?.(town.id), [props, town.id]);
  // Select, then open - the map's own rule for a town you own, and the only one
  // the panel advertises. A double click still works, but nothing on the row
  // says it is there, so the roster used to be a dead end for anyone who had
  // not read the source.
  const selected = props.selected;
  const select = useCallback(() => {
    props.onSelect(town.id);
    if (selected) open();
  }, [props, town.id, selected, open]);
  const enter = useCallback(() => props.onHover?.({ kind: "TOWN", id: town.id }), [props, town.id]);

  const detail = town.detail;
  const visiting = detail?.visitingHero ?? null;
  const built = detail?.builtThisTurn ?? false;

  return (
    <button
      type="button"
      className={cx("hm-entity-row", props.selected && "hm-entity-row--selected")}
      onClick={select}
      onDoubleClick={open}
      onPointerEnter={enter}
      aria-pressed={props.selected}
      title={`${town.name} - ${props.factionName}`}
    >
      {/* The same mount the hero roster's face wears - a town tile is a picture
          set into the column too, and two adjacent rows drawing the same object
          two different ways is what made the racks read as unrelated widgets. */}
      <Frame as="span" well className="hm-adv-portrait" style={{ padding: 0, flex: "0 0 auto", lineHeight: 0 }}>
        <Icon atlasKey={`town.${town.faction}.castle`} size={METRICS.portraitSmall} />
      </Frame>

      <span className="hm-col hm-grow" style={{ gap: "calc(var(--hm-u) * 1)" }}>
        {/* Stamped, like the hero roster's name and for the same reason: a town
            name set in the kit's serif beside a column of bitmap readings is the
            serif-among-bitmap tell, and the face carries a real lowercase now. */}
        <span className="hm-row hm-clip" style={{ gap: "calc(var(--hm-u) * 3)" }}>
          <span className="hm-pip" style={{ backgroundColor: props.colour }} />
          <BitmapText face={romanFaceForStep(props.nameScale)} scale={1}>{town.name}</BitmapText>
        </span>
        {/* One mark, one face: the faction line used to be the kit's serif
            under a bitmap town name, which is the same mismatch the hero
            roster carried directly above it. */}
        <span className="hm-adv-roster__state hm-clip">
          <BitmapText face="roman" scale={1} shadow>
            {props.factionName
              + (visiting !== null ? " - hero inside" : "")
              + (built ? " - built today" : "")}
          </BitmapText>
        </span>
      </span>
    </button>
  );
}
